import {Stack} from '@commercial-helios/ui/src/components/stack'
import {radii, tokens} from '@commercial-helios/ui/theme'
import * as EmailValidator from 'email-validator'
import ky from 'ky'
import React from 'react'
import styled from 'styled-components'

import type {ClearbitCompany} from '@/components/company-input'
import CompanyInput from '@/components/company-input'
import type {ExpenseReportGeneratorQuery} from '@/graphql-types'
import {useWizardContext} from '@/hooks/use-step-wizard'
import {sendExpenseReportEmail} from '@/utils/customer-io'
import {generatePDF} from '@/utils/html-to-pdf'
import {scrollToTop} from '@/utils/scroll'
import Button from '@helios/shared/components/button'
import Image from '@helios/shared/components/image'
import Input from '@helios/shared/components/input'
import RichText from '@helios/shared/components/rich-text'
import useFormErrors from '@helios/shared/hooks/use-form-errors'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {useLocale} from '@helios/shared/providers/locale'
import {BackgroundWrapper} from '@helios/shared/styles/grid'
import {Text, text} from '@helios/shared/styles/typography'
import type {PrismicData} from '@helios/shared/types/prismic-data'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import mq from '@helios/shared/utils/media-query'

import {BREAKPOINT_SM} from '../constants'
import type {FormsDataState, Dispatch} from '../expense-report-generator'
import {ActionType} from '../expense-report-generator'
import Loading from '../loading'
import type {ExpenseReportGeneratorVariant} from '../types'

interface Props {
    formsDataState: FormsDataState
    dispatch: Dispatch
    content: PrismicData<ExpenseReportGeneratorQuery, 'allPrismicExpenseReportGenerator'>['data']
    setShareableLink: (value: string) => void
    variant: ExpenseReportGeneratorVariant
}

const AlmostThere = ({formsDataState, dispatch, content, setShareableLink, variant}: Props) => {
    const contentAlmostThere = content.almostThere[0]
    const contentEmailContent = content.emailContent[0]
    const {back, company_name} = useGlobalStrings()
    const {navigate} = useWizardContext()
    const [loading, setLoading] = React.useState(false)
    const {country} = useLocale()
    const {errors, setError, clearError} = useFormErrors(formsDataState)
    const globalStrings = useGlobalStrings()

    const {emailSubject, helloThere, emailBody, explorePleoButtonText, explorePleoButtonLink} =
        contentEmailContent

    if (!('url' in explorePleoButtonLink)) {
        throw Error('URL for the Explore Pleo button in Expense report generator is missing!')
    }

    React.useEffect(() => {
        track(TrackingEvent.ERGActioned, {
            action: 'almost_there_step_reached',
            variant,
        })
    }, [variant])

    const handleGenerateReport = async () => {
        setLoading(true)

        try {
            const sharableLink = await generatePDF('expense-report-generator', formsDataState)
            setShareableLink(sharableLink)

            track(TrackingEvent.ERGActioned, {
                action: 'pdf_generated',
                variant,
            })

            setLoading(false)
            navigate.next()
        } catch (error) {
            setLoading(false)
            console.error(error)
        }
    }

    const handleEmailExpenseReport = async () => {
        if (!formsDataState.email) {
            setError('email', globalStrings.this_field_is_required)
            return
        }

        if (!EmailValidator.validate(formsDataState.email)) {
            setError('email', globalStrings.invalid_email_error)
            return
        }

        setLoading(true)

        try {
            const shareableLink = await generatePDF('expense-report-generator', formsDataState)

            track(TrackingEvent.ERGActioned, {
                action: 'pdf_generated',
                variant,
            })

            const attachmentData = await ky.get(shareableLink, {timeout: 30000}).arrayBuffer()

            await sendExpenseReportEmail({
                to: formsDataState.email,
                subject: `${emailSubject} ${formsDataState.companyName}`,
                messageData: {
                    customerName: formsDataState.fullName,
                    country,
                    helloThere,
                    emailBody,
                    explorePleoButtonText,
                    explorePleoButtonLink:
                        'url' in explorePleoButtonLink && explorePleoButtonLink.url,
                },
                customerID: window.analytics?.user().anonymousId(),
                attachments: [
                    {
                        fileName: `${content.expenseReport} - ${formsDataState.reportNumber}.pdf`,
                        data: attachmentData,
                    },
                ],
            })

            setLoading(false)
            navigate.next()
        } catch (error) {
            setLoading(false)
            console.error(error)
        }
    }

    const handleCompanyInputChange = (companyName: string) => {
        dispatch({
            type: ActionType.COMPANY_NAME_UPDATED,
            value: companyName,
        })
        dispatch({
            type: ActionType.COMPANY_LOGO_URL_UPDATED,
            value: null,
        })
    }

    const handleCompanyInputSelect = (company: ClearbitCompany) => {
        dispatch({
            type: ActionType.COMPANY_NAME_UPDATED,
            value: company.name,
        })
        dispatch({
            type: ActionType.COMPANY_LOGO_URL_UPDATED,
            value: company.logo,
        })
    }

    if (loading) {
        scrollToTop()

        return <Loading content={content} loadingImage={content.loadingImage} />
    }

    return (
        <Card>
            <TextBlock>
                <MainText>
                    <Text variant="title-base">{contentAlmostThere.title}</Text>

                    <BodyWrapper>
                        <RichText
                            variant="paragraph-large"
                            color="muted"
                            content={contentAlmostThere.body.richText}
                        />

                        <DesktopDisclaimer>
                            <RichText
                                variant="label-base"
                                color="lightMuted"
                                content={contentAlmostThere.disclaimer.richText}
                            />
                        </DesktopDisclaimer>
                    </BodyWrapper>
                </MainText>

                <PreviousStepLink onClick={navigate.prev}>{back}</PreviousStepLink>
            </TextBlock>

            <Stack space={60} align="right">
                <Form>
                    {!formsDataState.companyLogoUrl ? (
                        <LogoWrapper color="pink">
                            <Image image={contentAlmostThere.logoPlaceholder} width={120} />
                        </LogoWrapper>
                    ) : (
                        <CompanyLogo>
                            <img
                                src={`${formsDataState.companyLogoUrl}?size=240`}
                                alt=""
                                width={120}
                            />
                        </CompanyLogo>
                    )}

                    <div>
                        <CompanyInput
                            label={company_name}
                            placeholder={contentAlmostThere.yourCompanyName}
                            value={formsDataState.companyName}
                            onChange={(companyName) => {
                                handleCompanyInputChange(companyName)
                            }}
                            onSelect={(company: ClearbitCompany) => {
                                handleCompanyInputSelect(company)
                            }}
                        />
                    </div>

                    <div>
                        <Input
                            label={contentAlmostThere.yourName}
                            placeholder={contentAlmostThere.yourFirstAndLastName}
                            value={formsDataState.fullName}
                            onChange={(value) => {
                                dispatch({
                                    type: ActionType.FULL_NAME_UPDATED,
                                    value,
                                })
                            }}
                        />
                    </div>

                    <div>
                        <Input
                            label={contentAlmostThere.reportNumber}
                            placeholder={contentAlmostThere.yourReportNumber}
                            value={formsDataState.reportNumber}
                            onChange={(value) => {
                                dispatch({
                                    type: ActionType.REPORT_NUMBER_UPDATED,
                                    value,
                                })
                            }}
                        />
                    </div>

                    {variant === 'external' && (
                        <div>
                            <Input
                                label={globalStrings.work_email}
                                placeholder={globalStrings.your_work_email}
                                value={formsDataState.email}
                                onChange={(value: string) => {
                                    dispatch({
                                        type: ActionType.EMAIL_UPDATED,
                                        value,
                                    })
                                    clearError('email')
                                }}
                                error={errors.email}
                            />
                        </div>
                    )}

                    <MobileDisclaimer>
                        <RichText
                            variant="label-base"
                            color="lightMuted"
                            content={contentAlmostThere.disclaimer.richText}
                        />
                    </MobileDisclaimer>
                </Form>

                <ButtonsWrapper>
                    {variant === 'internal' && (
                        <Button onClick={handleGenerateReport}>
                            {contentAlmostThere.generateReportButtonText}
                        </Button>
                    )}

                    {variant === 'external' && (
                        <Button onClick={handleEmailExpenseReport}>
                            {contentAlmostThere.receiveReportButtonText}
                        </Button>
                    )}
                </ButtonsWrapper>
            </Stack>
        </Card>
    )
}

const Card = styled.div`
    display: grid;
    padding: 0;
    margin: 0 auto;
    background: ${tokens.shade000};
    row-gap: ${tokens.spacing40};
    column-gap: ${tokens.spacing40};

    ${mq.min(BREAKPOINT_SM)} {
        padding: ${tokens.spacing40};
        border-radius: ${radii.arc8};
        grid-template-columns: 1fr 1fr;
    }
`

const TextBlock = styled.div`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min(BREAKPOINT_SM)} {
        grid-template-rows: 1fr auto;
    }
`

const MainText = styled.div`
    display: grid;
    gap: ${tokens.spacing24};
    grid-template-rows: auto 1fr;
`

const BodyWrapper = styled.div`
    ${mq.min('lg')} {
        max-width: 85%;
    }
`

const MobileDisclaimer = styled.div`
    ${mq.min(BREAKPOINT_SM)} {
        display: none;
    }
`

const DesktopDisclaimer = styled.div`
    display: none;

    ${mq.min(BREAKPOINT_SM)} {
        display: block;
        padding-top: ${tokens.spacing16};
    }
`

const PreviousStepLink = styled.button`
    ${text({variant: 'spezia'})}
    display: none;

    ${mq.min(BREAKPOINT_SM)} {
        display: block;
        width: max-content;
        cursor: pointer;
        background: none;
        border: none;
    }
`

const Form = styled.form`
    display: grid;
    gap: ${tokens.spacing20};
    width: 100%;

    ${mq.min('lg')} {
        gap: ${tokens.spacing28};
    }
`

const ButtonsWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing20};
    width: 100%;

    ${mq.min(BREAKPOINT_SM)} {
        grid-auto-flow: column;
        width: auto;
    }
`

const LogoWrapper = styled(BackgroundWrapper)`
    display: grid;
    align-items: center;
    width: 80px;
    height: 80px;
    padding: ${tokens.spacing10};
    border-radius: ${tokens.arc12};
    justify-content: center;

    ${mq.max(BREAKPOINT_SM)} {
        justify-self: center;
    }

    ${mq.min('md')} {
        width: 120px;
        height: 120px;
    }
`

const CompanyLogo = styled.div`
    display: grid;
    width: 80px;
    height: 80px;
    align-items: center;

    ${mq.max(BREAKPOINT_SM)} {
        justify-self: center;
    }

    ${mq.min('md')} {
        width: 120px;
        height: 120px;
    }

    img {
        border-radius: ${tokens.arc12};
    }
`

export default AlmostThere
